import React, { useState, useEffect } from 'react';
import courseService from '../services/courseService';

const EnrollmentModal = ({ isOpen, course, onClose }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [accountConfirmed, setAccountConfirmed] = useState(false);

    // Reset modal state when it opens/closes or course changes
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(1);
            setAccountConfirmed(false);
            // Prevent scrolling on the body
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        // Cleanup function to restore scrolling when component unmounts
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen, course]);

    const goToStep = (stepNumber) => {
        setCurrentStep(stepNumber);
    };

    const handleAccountConfirmChange = (e) => {
        setAccountConfirmed(e.target.checked);
    };

    const checkAccountAndContinue = () => {
        if (accountConfirmed) {
            goToStep(3);
        } else {
            alert('Please confirm that you have created an account or already have one before continuing.');
        }
    };

    const completeEnrollment = () => {
        if (course) {
            // Redirect to the Moodle enrollment page after the 3-step process
            window.open(`https://online.sparrowportal.co.za/moodle/course/view.php?id=${course.id}`, '_blank');
            onClose();
        }
    };

    const getStepClass = (stepNumber) => {
        if (stepNumber === currentStep) return 'step active';
        if (stepNumber < currentStep) return 'step completed';
        return 'step';
    };

    const getStepContentClass = (stepNumber) => {
        return stepNumber === currentStep ? 'step-content active' : 'step-content';
    };

    if (!isOpen || !course) return null;

    const courseSummary = courseService.stripHtml(course.summary || 'No description available');

    return (
        <div className={`modal-overlay ${isOpen ? 'active' : ''}`}>
            <div className="enrollment-modal">
                <div className="modal-header">
                    <h2>Course Enrollment Process</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="modal-body">
                    <div className="enrollment-steps">
                        <div className="step-line"></div>
                        <div className={getStepClass(1)}>
                            <div className="step-number">1</div>
                            <div className="step-title">Review Course</div>
                        </div>
                        <div className={getStepClass(2)}>
                            <div className="step-number">2</div>
                            <div className="step-title">Account Setup</div>
                        </div>
                        <div className={getStepClass(3)}>
                            <div className="step-number">3</div>
                            <div className="step-title">Payment</div>
                        </div>
                    </div>
                    
                    <div className={getStepContentClass(1)}>
                        <h3>{course.fullname}</h3>
                        <p>{courseSummary}</p>
                        <div className="step-actions">
                            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                            <button className="btn btn-primary" onClick={() => goToStep(2)}>Continue to Account Setup</button>
                        </div>
                    </div>
                    
                    <div className={getStepContentClass(2)}>
                        <h3>Create or Sign In to Your Account</h3>
                        <p>Before enrolling in this course, you need to have an account with Sparrow Schools. If you already have an account, you'll be asked to sign in.</p>
                        <p>Create an account <a href="https://online.sparrowportal.co.za/moodle/login/signup.php" target="_blank" rel="noopener noreferrer">here</a></p>
                        
                        <div className="account-confirmation">
                            <label className="checkbox-container">
                                <input 
                                    type="checkbox" 
                                    checked={accountConfirmed}
                                    onChange={handleAccountConfirmChange}
                                />
                                <span>I confirm that I have created an account or already have one</span>
                            </label>
                        </div>
                        
                        <div className="step-actions">
                            <button className="btn btn-secondary" onClick={() => goToStep(1)}>Back</button>
                            <button 
                                className={`btn btn-primary ${!accountConfirmed ? 'disabled' : ''}`}
                                onClick={checkAccountAndContinue}
                                disabled={!accountConfirmed}
                            >
                                Continue to Payment
                            </button>
                        </div>
                    </div>
                    
                    <div className={getStepContentClass(3)}>
                        <h3>Complete Your Payment</h3>
                        <p>You're almost done! In this final step, you'll complete your payment via PayFast to secure your enrollment.</p>
                        <p>Payment options include:</p>
                        <ul>
                            <li>Credit/Debit Card</li>
                            <li>Instant EFT</li>
                        </ul>
                        <p><strong>Note:</strong> After completing your payment, you'll receive a confirmation email with instructions on how to access your course.</p>
                        <div className="step-actions">
                            <button className="btn btn-secondary" onClick={() => goToStep(2)}>Back</button>
                            <button className="btn btn-primary" onClick={completeEnrollment}>Complete Enrollment</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnrollmentModal;
